import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Play, Clapperboard, AlertTriangle, RefreshCw, Search, Trash2 } from 'lucide-react';
import './VideosPage.css';
import { useAuthContext } from './components/AuthProvider';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import RemixVideoModal from './components/RemixVideoModal';
import { listVideoLibrary, getVideoModelSpecs, deleteVideo, deleteStandaloneVideo } from './lib/api';
import { prettyModelLabel } from './lib/videoUi';
import { useVideoLibraryData, useVideoLibraryStore } from './stores';

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 300;
const GENERATING_POLL_MS = 8000;
const SKELETON_COUNT = 12;
/** Keep in sync with the `videos-puff` keyframes in VideosPage.css. */
const PUFF_MS = 320;

/** Ratio labels we snap to so badges read as "9:16" instead of "1078:1920". */
const KNOWN_RATIOS = [
  { label: '16:9', value: 16 / 9 },
  { label: '9:16', value: 9 / 16 },
  { label: '1:1', value: 1 },
  { label: '4:5', value: 4 / 5 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 },
  { label: '21:9', value: 21 / 9 },
];

const ratioLabelFromValue = (ratio) => {
  if (!Number.isFinite(ratio) || ratio <= 0) return null;
  const match = KNOWN_RATIOS.reduce(
    (best, candidate) => {
      const delta = Math.abs(candidate.value - ratio) / candidate.value;
      return delta < best.delta ? { label: candidate.label, delta } : best;
    },
    { label: null, delta: Infinity }
  );
  return match.delta <= 0.08 ? match.label : null;
};

const ratioLabel = (video, mediaSize) => {
  if (mediaSize) return ratioLabelFromValue(mediaSize.width / mediaSize.height);
  if (typeof video.aspectRatio === 'string' && /^\d+:\d+$/.test(video.aspectRatio.trim())) {
    const [w, h] = video.aspectRatio.trim().split(':').map(Number);
    return w > 0 && h > 0 ? ratioLabelFromValue(w / h) : null;
  }
  return null;
};

const formatDuration = (durationMs) => {
  if (!Number.isFinite(durationMs) || durationMs <= 0) return null;
  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const formatRelativeDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

function VideoCard({ video, onClick, onDelete, isRemoving }) {
  const videoRef = useRef(null);
  const cancelRef = useRef(null);
  const [mediaSize, setMediaSize] = useState(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const isGenerating = video.status === 'generating';
  const isFailed = video.status === 'failed' || video.status === 'error';
  const title = video.prompt || video.name || 'Untitled video';
  const duration = formatDuration(video.durationMs);
  const ratio = ratioLabel(video, mediaSize);

  // Nudge past frame 0 so the tile paints a still instead of a black box while paused.
  const previewSrc = useMemo(() => {
    if (!video.videoUrl) return null;
    return video.videoUrl.includes('#') ? video.videoUrl : `${video.videoUrl}#t=0.1`;
  }, [video.videoUrl]);

  useEffect(() => {
    setMediaSize(null);
  }, [video.videoUrl]);

  const handleMouseEnter = () => {
    const el = videoRef.current;
    if (el && el.paused) {
      el.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    const el = videoRef.current;
    if (el && !el.paused) {
      el.pause();
      try {
        el.currentTime = 0.1;
      } catch (_) {
        // Streamed URLs can reject seeking before buffering.
      }
    }
  };

  const handleLoadedMetadata = (event) => {
    const { videoWidth, videoHeight } = event.currentTarget;
    if (videoWidth > 0 && videoHeight > 0) {
      setMediaSize({ width: videoWidth, height: videoHeight });
    }
  };

  // Escape backs out of the confirm step; focus lands on Cancel so the
  // destructive button is never the one sitting under a stray Enter.
  useEffect(() => {
    if (!isConfirmingDelete) return;
    cancelRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsConfirmingDelete(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isConfirmingDelete]);

  const handleConfirmDelete = () => {
    setIsConfirmingDelete(false);
    onDelete(video);
  };

  return (
    <div
      className={`videos-card${isGenerating ? ' videos-card--generating' : ''}${isFailed ? ' videos-card--failed' : ''}${isRemoving ? ' videos-card--removing' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className="videos-card__surface"
        onClick={() => onClick(video)}
        disabled={isGenerating || isConfirmingDelete}
        aria-label={isGenerating ? `${title} — generating` : `Remix ${title}`}
      >
        <div className="videos-card__frame">
          {previewSrc ? (
            <video
              ref={videoRef}
              className="videos-card__media"
              src={previewSrc}
              muted
              loop
              playsInline
              preload="metadata"
              onLoadedMetadata={handleLoadedMetadata}
            />
          ) : (
            <div className="videos-card__placeholder">
              {isGenerating ? (
                <div className="videos-card__shimmer" />
              ) : (
                <>
                  {isFailed ? <AlertTriangle size={20} /> : <Clapperboard size={20} />}
                  <span className="videos-card__placeholder-text">
                    {isFailed ? 'Generation failed' : 'No preview'}
                  </span>
                </>
              )}
            </div>
          )}

          {/* The placeholder already spells out a failure, so the badge is only
              needed when a failed video still has a preview to show. */}
          {(isGenerating || (isFailed && previewSrc)) && (
            <span className={`videos-card__status${isFailed ? ' videos-card__status--failed' : ''}`}>
              {isGenerating && <span className="videos-card__status-dot" />}
              {isGenerating ? 'Generating' : 'Failed'}
            </span>
          )}

          {previewSrc && (
            <>
              <span className="videos-card__play" aria-hidden="true">
                <Play size={14} fill="currentColor" />
              </span>
              <span className="videos-card__remix">
                <RefreshCw size={11} />
                Remix
              </span>
              <div className="videos-card__chips">
                {ratio && <span className="videos-card__chip">{ratio}</span>}
                {duration && <span className="videos-card__chip">{duration}</span>}
              </div>
            </>
          )}
        </div>

        <div className="videos-card__body">
          <p className="videos-card__prompt" title={title}>
            {title}
          </p>
          <div className="videos-card__meta">
            <span className="videos-card__model">
              {prettyModelLabel(video.modelLabel, video.model)}
            </span>
            <span className="videos-card__time">{formatRelativeDate(video.createdAt)}</span>
          </div>
        </div>
      </button>

      {!isConfirmingDelete && (
        <button
          type="button"
          className="videos-card__delete"
          onClick={() => setIsConfirmingDelete(true)}
          aria-label={`Delete ${title}`}
          title="Delete video"
        >
          <Trash2 size={13} />
        </button>
      )}

      {isConfirmingDelete && (
        <div className="videos-card__confirm" role="alertdialog" aria-label="Confirm delete">
          <p className="videos-card__confirm-text">Delete this video?</p>
          <div className="videos-card__confirm-actions">
            <button
              ref={cancelRef}
              type="button"
              className="videos-card__confirm-button"
              onClick={() => setIsConfirmingDelete(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="videos-card__confirm-button videos-card__confirm-button--danger"
              onClick={handleConfirmDelete}
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function VideosPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuthContext();
  const { items: cachedVideos, hasCache } = useVideoLibraryData(user?.id);

  const [videos, setVideos] = useState(() => cachedVideos);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [remixTarget, setRemixTarget] = useState(null);
  const [modelSpecs, setModelSpecs] = useState(null);
  const [removingIds, setRemovingIds] = useState(() => new Set());

  const searchTimeoutRef = useRef(null);
  const puffTimeoutsRef = useRef([]);

  useEffect(() => () => puffTimeoutsRef.current.forEach(clearTimeout), []);

  useEffect(() => {
    if (hasCache && videos.length === 0 && !debouncedSearch) {
      setVideos(cachedVideos);
      setIsLoading(false);
    }
  }, [cachedVideos, hasCache, videos.length, debouncedSearch]);

  const fetchVideos = useCallback(async ({ page = 1, search = '', silent = false, merge = false } = {}) => {
    if (!user || loading) return;
    try {
      if (!silent) setIsLoading(page === 1);
      setError(null);
      const response = await listVideoLibrary({ page, limit: PAGE_SIZE, search: search || null });
      const fetched = response.videos || [];
      if (page === 1 && !search) {
        const cache = useVideoLibraryStore.getState();
        cache.setLibraryVideos(fetched);
        cache.setHasFetched(true);
        cache.setCachedForUserId(user.id);
        cache.setError(null);
      }
      setVideos((prev) => {
        if (page > 1) return [...prev, ...fetched];
        if (!merge) return fetched;
        // Poll refresh: update in place and prepend anything new so
        // "Load more" results already on screen aren't dropped. Once the API
        // has returned the durable row for a newly created project, discard
        // its client-only placeholder; the two records intentionally have
        // different IDs, so an ID-only merge would leave "Generating" behind
        // forever beside the completed video.
        const byId = new Map(fetched.map((v) => [v.id, v]));
        const durableProjectIds = new Set(
          fetched.filter((v) => !v.isOptimistic && v.projectId).map((v) => v.projectId)
        );
        const updated = prev
          .filter((v) => !(v.isOptimistic && durableProjectIds.has(v.projectId)))
          .map((v) => byId.get(v.id) || v);
        const existingIds = new Set(prev.map((v) => v.id));
        const fresh = fetched.filter((v) => !existingIds.has(v.id));
        return [...fresh, ...updated];
      });
      if (page > 1 || !merge) setPagination(response.pagination || null);
    } catch (err) {
      console.error('Failed to fetch videos:', err);
      if (!silent) setError('Failed to load videos');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [user, loading]);

  // Initial load + on search change
  useEffect(() => {
    fetchVideos({ page: 1, search: debouncedSearch });
  }, [debouncedSearch, fetchVideos]);

  // Preload model specs for the remix modal
  useEffect(() => {
    if (!user || loading) return;
    getVideoModelSpecs()
      .then(setModelSpecs)
      .catch((err) => console.error('Failed to fetch model specs:', err));
  }, [user, loading]);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchQuery]);

  // Poll while anything is generating so remixes/creations resolve in place.
  const hasGenerating = useMemo(() => videos.some((v) => v.status === 'generating'), [videos]);
  useEffect(() => {
    if (!hasGenerating) return;
    const intervalId = setInterval(() => {
      fetchVideos({ page: 1, search: debouncedSearch, silent: true, merge: true });
    }, GENERATING_POLL_MS);
    return () => clearInterval(intervalId);
  }, [hasGenerating, debouncedSearch, fetchVideos]);

  const handleLoadMore = () => {
    if (!pagination?.hasNext || isLoadingMore) return;
    setIsLoadingMore(true);
    fetchVideos({ page: pagination.page + 1, search: debouncedSearch, silent: true });
  };

  const markRemoving = (videoId, removing) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      if (removing) next.add(videoId);
      else next.delete(videoId);
      return next;
    });
  };

  const handleDelete = async (video) => {
    // The puff plays while the request is in flight; the card only leaves the
    // grid once the delete actually lands.
    markRemoving(video.id, true);
    const startedAt = Date.now();
    try {
      if (video.projectId) await deleteVideo(video.projectId, null, video.id);
      else await deleteStandaloneVideo(video.id);
      const timeoutId = setTimeout(() => {
        setVideos((prev) => prev.filter((v) => v.id !== video.id));
        useVideoLibraryStore.getState().removeLibraryVideo(video.id);
        setPagination((prev) =>
          prev ? { ...prev, totalCount: Math.max(0, prev.totalCount - 1) } : prev
        );
        markRemoving(video.id, false);
      }, Math.max(0, PUFF_MS - (Date.now() - startedAt)));
      puffTimeoutsRef.current.push(timeoutId);
    } catch (err) {
      console.error('Failed to delete video:', err);
      markRemoving(video.id, false);
      setError('Failed to delete video');
    }
  };

  const handleRemixStarted = (newVideo) => {
    setRemixTarget(null);
    if (newVideo) {
      setVideos((prev) => [newVideo, ...prev]);
      useVideoLibraryStore.getState().addLibraryVideo(newVideo);
    } else {
      fetchVideos({ page: 1, search: debouncedSearch, silent: true });
    }
  };

  const hasNoVideosAtAll = !isLoading && videos.length === 0 && !debouncedSearch;

  return (
    <AuthenticatedLayout>
      <div className="videos-content">
        <div className="videos-header-section">
          <div className="videos-header-copy">
            <h1 className="videos-title">Videos</h1>
            <p className="videos-subtitle">Every video you've generated, across all projects.</p>
          </div>
          <button
            type="button"
            className="videos-create-button"
            onClick={() => navigate('/videos/create')}
          >
            <Plus size={16} />
            Create Video
          </button>
        </div>

        {error && <div className="videos-error">{error}</div>}

        {isLoading && videos.length === 0 ? (
          <div className="videos-grid videos-grid--skeleton" aria-hidden="true">
            {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <div key={index} className="videos-skeleton">
                <div className="videos-skeleton__frame" />
                <div className="videos-skeleton__body">
                  <span className="videos-skeleton__line" />
                  <span className="videos-skeleton__line videos-skeleton__line--short" />
                </div>
              </div>
            ))}
          </div>
        ) : hasNoVideosAtAll ? (
          <div className="videos-empty-state">
            <div className="videos-empty-icon">
              <Play size={24} />
            </div>
            <h2>No videos yet</h2>
            <p>Generate your first video and it will show up here.</p>
            <button
              type="button"
              className="videos-create-button videos-create-button--large"
              onClick={() => navigate('/videos/create')}
            >
              <Plus size={16} />
              Create Video
            </button>
          </div>
        ) : (
          <>
            <div className="videos-toolbar">
              <div className="videos-search">
                <Search size={14} className="videos-search__icon" />
                <input
                  type="text"
                  className="videos-search__input"
                  placeholder="Search by prompt or project…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {pagination && (
                <span className="videos-count">
                  {pagination.totalCount} {pagination.totalCount === 1 ? 'video' : 'videos'}
                </span>
              )}
            </div>

            {videos.length === 0 ? (
              <div className="videos-empty-state">
                <h2>No videos found</h2>
                <p>Try a different search.</p>
              </div>
            ) : (
              <div className="videos-grid">
                {videos.map((video) => (
                  <VideoCard
                    key={video.id}
                    video={video}
                    onClick={setRemixTarget}
                    onDelete={handleDelete}
                    isRemoving={removingIds.has(video.id)}
                  />
                ))}
              </div>
            )}

            {pagination?.hasNext && (
              <div className="videos-load-more-row">
                <button
                  type="button"
                  className="videos-load-more"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {remixTarget && (
        <RemixVideoModal
          video={remixTarget}
          modelSpecs={modelSpecs}
          onClose={() => setRemixTarget(null)}
          onRemixStarted={handleRemixStarted}
        />
      )}
    </AuthenticatedLayout>
  );
}

export default VideosPage;
